import * as moment from 'moment';
import v4 from 'uuid/v4';

import { IClipComponent } from '~services/clip/types';
import { ISettingsSettings } from '~services/settings';
import { milliSecsToSecs, secsToMilliSecs } from '~services/utilities';
import { IExportClipWithMeta, ITimelineClip } from '~src/store/timeline/clip/types';
import { Base64ImageUri, MilliSeconds, Seconds, V4Uid } from '~src/types';

interface IRange {
    start: number;
    end: number;
}

const createRange = (start: number, end: number): IRange => ({
    start,
    end,
});

const isRangeIntersecting = (rangeA: IRange, rangeB: IRange): boolean => {
    const maxRangeStart = Math.max(rangeA.start, rangeB.start);

    // handle clip intersection with a single point where end can be null
    const rangeEndA = rangeA.end === null ? rangeA.start : rangeA.end;
    const rangeEndB = rangeB.end === null ? rangeB.start : rangeB.end;

    const minRangeEnd = Math.min(rangeEndA, rangeEndB);

    return maxRangeStart <= minRangeEnd;
};

const createTimelineClip = (renderClipInTime: Seconds, exportClipInTime: Seconds, thumbnailId: V4Uid = null): ITimelineClip => ({
    id: v4(),
    title: `Clip - ${moment().format('DD/MM/YYYY @ HHmm')}`,
    thumbnailId,

    // in marker times
    renderClipInTime,
    exportClipInTime,

    // out marker times
    renderClipOutTime: null,
    exportClipOutTime: null,
});

const getClipById = (clips: ITimelineClip[], id: ITimelineClip['id']): ITimelineClip => {
    return clips.find((clip) => clip.id === id);
};

const getExportableClips = (clips: ITimelineClip[], minimumClipDuration: ISettingsSettings['minimumClipDuration']): ITimelineClip[] => {
    return clips.filter(({ exportClipInTime, exportClipOutTime }) => {
        if (exportClipOutTime === null) {
            return false;
        }
        const clipDuration = Math.abs(exportClipInTime - exportClipOutTime);
        return clipDuration > minimumClipDuration;
    });
};

const isClipValid = (
    { exportClipInTime, exportClipOutTime }: ITimelineClip,
    minimumClipDuration: ISettingsSettings['minimumClipDuration']
): { valid: boolean; message?: string } => {
    if (exportClipOutTime === null) {
        return {
            valid: false,
            message: "This clip doesn't have an out marker set",
        };
    } else if (Math.abs(exportClipInTime - exportClipOutTime) < minimumClipDuration) {
        return {
            valid: false,
            message: `The duration of the clip needs to be greater than ${minimumClipDuration} seconds`,
        };
    }

    return {
        valid: true,
    };
};

export interface IPdtFragment {
    sn?: number;
    start: Seconds;
    programDateTime: MilliSeconds | null;
}

/**
 * merges a newly loaded playlist's fragments into the known ones, keyed by media sequence number.
 * a sliding live window drops old fragments, but clips marked on them still need their PDT.
 * newly loaded fragments win, since hls.js may have refined their start from the parsed media.
 */
const mergePdtFragments = (known: IPdtFragment[], loaded: IPdtFragment[]): IPdtFragment[] => {
    const bySn = new Map<number, IPdtFragment>(known.map((frag): [number, IPdtFragment] => [frag.sn, frag]));
    loaded.forEach(({ sn, start, programDateTime }) => bySn.set(sn, { sn, start, programDateTime }));

    return Array.from(bySn.values()).sort((a, b) => a.start - b.start);
};

/**
 * Converts a player (media element) time into seconds of wall clock time since `loadPDT`.
 * The player timeline is the sum of segment durations and ignores PDT gaps at discontinuities,
 * so the PDT of the fragment containing `mediaTime` is used as the anchor.
 * Falls back to `mediaTime` when no fragment with a PDT contains it.
 */
const toWallClockTime = (mediaTime: Seconds, fragments: IPdtFragment[], loadPDT: MilliSeconds): Seconds => {
    const fragment = fragments.filter((frag) => frag.start <= mediaTime).pop();

    if (!fragment || !fragment.programDateTime) {
        return mediaTime;
    }

    return milliSecsToSecs(fragment.programDateTime + secsToMilliSecs(mediaTime - fragment.start) - loadPDT);
};

// todo: revisit when refactor of ITimelineClip
interface IExportClipWithMetaPayload {
    id?: V4Uid;
    title?: string;
    exportClipInTime?: number;
    exportClipOutTime?: number;
    thumbnail?: string;
    loadPDT?: number;
    pdtFragments?: IPdtFragment[];
}

class ClipUtilities {
    private static MINIMUM_COMPONENT_COUNT = 1;

    private static buildComponent(
        playerInTime: Seconds,
        playerOutTime: Seconds,
        pdt: MilliSeconds,
        pdtFragments: IPdtFragment[]
    ): IClipComponent {
        if (!playerInTime) {
            return undefined;
        }

        const inTime = pdt ? toWallClockTime(playerInTime, pdtFragments, pdt) : playerInTime;
        const outTime = pdt ? toWallClockTime(playerOutTime, pdtFragments, pdt) : playerOutTime;
        const offsetMS = secsToMilliSecs(inTime);
        const duration = outTime - inTime;
        const durationMS = secsToMilliSecs(duration);

        const component: IClipComponent = {
            offsetMS,
            durationMS,
        };

        if (pdt) {
            component.startTimeMS = pdt;
        }

        return component;
    }

    public static isCombined(clip: IExportClipWithMeta): boolean {
        return ClipUtilities.getComponentCount(clip) > ClipUtilities.MINIMUM_COMPONENT_COUNT;
    }

    public static getComponentCount(clip: IExportClipWithMeta): number {
        return clip.components.length;
    }

    public static buildExportClipWithMeta(payload: IExportClipWithMetaPayload = {}): IExportClipWithMeta {
        const {
            id = v4(),
            title = null,
            exportClipInTime = null,
            exportClipOutTime = null,
            thumbnail = null,
            loadPDT = null,
            pdtFragments = [],
        } = payload;

        const clipTitle = title !== null ? title : `Combined clip - ${moment().format('DD/MM/YYYY @ HHmm')}`;
        const component = ClipUtilities.buildComponent(exportClipInTime, exportClipOutTime, loadPDT, pdtFragments);

        /**
         * the component spans wall clock time, which includes any PDT gap at a discontinuity.
         * the backend skips the gap when cutting, so the clip's real length is the player duration.
         */
        const duration = component ? exportClipOutTime - exportClipInTime : 0;

        return {
            components: component ? [component] : [],
            meta: {
                id,
                title: clipTitle,
                thumbnail,
                duration,
            },
        };
    }

    public static combineClipComponents(targetClip: IExportClipWithMeta, sourceClips: IExportClipWithMeta[]): IExportClipWithMeta {
        const components = sourceClips.map((clip) => clip.components[0]).sort((a, b) => a.offsetMS - b.offsetMS);

        const duration = sourceClips.reduce((accumulator, clip) => {
            return accumulator + clip.meta.duration;
        }, 0);

        targetClip.components = components;
        targetClip.meta.duration = duration;

        return targetClip;
    }

    public static setTitle(title: string, clip: Readonly<IExportClipWithMeta>): IExportClipWithMeta {
        return {
            ...clip,
            meta: {
                ...clip.meta,
                title,
            },
        };
    }

    public static setThumbnail(thumbnail: Base64ImageUri, clip: Readonly<IExportClipWithMeta>): IExportClipWithMeta {
        return {
            ...clip,
            meta: {
                ...clip.meta,
                thumbnail,
            },
        };
    }

    /* enforce static usage: prevent instances being created */
    private constructor() {
        /* noop */
    }
}

export {
    // move these to ClipUtilities
    isRangeIntersecting,
    createRange,
    createTimelineClip,
    getClipById,
    getExportableClips,
    isClipValid,
    mergePdtFragments,
    toWallClockTime,
    ClipUtilities,
};
